import { FormikProps } from 'formik';
import { TFunction } from 'react-i18next';
import { FormData } from '../feature/webinar-creator-wizard';
import { ImageUploader } from '../utils/image-uploader';
import styled from '@emotion/styled';
import { StyledButton, StyledLabel } from '@selfserve/react-ui';
import { MouseEvent, MouseEventHandler, useCallback, useContext, useState } from 'react';
import { KavaEventPrefix } from '@selfserve/react-utils';
import MarketingInfoContext from '../../../../react-feature-marketing-info/src/lib/utils/markerting-info-context';

interface Props {
  t: TFunction<'translation', undefined>;
  formik: FormikProps<FormData>;
}

const StyledThemeContainer = styled.div(({ theme }: { theme?: any }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  marginBottom: '24px',
  '& .theme-block-label': {
    width: '100%',
  },
  '& .theme-image': {
    width: '180px',
    margin: '10px',
    marginLeft: '0px',
  },
  '& .theme-label': {
    display: 'inline',
    marginRight: '5px',
  },
  '& .active-label': {
    display: 'inline',
    padding: '3px',
    borderRadius: '4px',
    backgroundColor: 'rgba(0, 110, 250, 0.1)',
    color: theme.palette.primary.main,
  },
  '& .theme-image:hover > .theme-overlay': {
    display: 'block',
  },
}));

const StyledImage = styled.div(({ image }: { image: string }) => ({
  height: '110px',
  backgroundImage: 'url(assets/' + image + '.svg)',
}));

const SelectThemeOverlay = styled.div(() => ({
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  height: '100%',
  display: 'none',
  borderRadius: '4px',
  textAlign: 'center',
}));

const SelectThemeButton = styled(StyledButton)(
  ({ theme }: { theme?: any }) => ({
    width: '120px',
    marginTop: '40px',
    fontSize: '14px',
    paddingRight: '0px',
    paddingLeft: '0px',
    height: '32px',
    color: theme.palette.common.black,
    backgroundColor: theme.palette.common.white,
    '&:hover': {
      backgroundColor: 'rgba(204, 204, 204, 1)',
    },
    '&.selected': {
      color: 'rgba(204, 204, 204, 1)',
      backgroundColor: 'rgba(204, 204, 204, 0)',
      borderColor: 'rgba(204, 204, 204, 1)',
      '&:hover': {
        backgroundColor: 'rgba(204, 204, 204, 0.5)',
      },
    },
  })
);

export const WebinarCreatorBranding = ({ t, formik }: Props) => {
  const { values, setFieldValue } = formik;
  const [isImageLoading, setImageLoading] = useState(false);

  const { raiseAnalyticEventsRequest } = useContext(MarketingInfoContext);
  const handleThemeChange = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const value = (event.target as HTMLInputElement).value;
      raiseAnalyticEventsRequest(
        KavaEventPrefix + value.charAt(0).toUpperCase() + value.slice(1)
      );
      setFieldValue('theme', value);
    },
    [setFieldValue]
  );

  const imageLoadingCallback = useCallback(
    (isLoading: boolean) => setImageLoading(isLoading),
    [setImageLoading]
  );

  const StyledThemeElement = ({
    label,
    image,
    type,
    handleThemeSelected,
  }: {
    label: string;
    image: string;
    type: string;
    handleThemeSelected: MouseEventHandler<HTMLButtonElement>;
  }) => {
    const selected = values.theme === type;
    return (
      <div>
        <StyledImage image={image} className="theme-image">
          <SelectThemeOverlay className="theme-overlay">
            <SelectThemeButton
              className={selected ? 'selected' : ''}
              onClick={handleThemeSelected}
              value={type}
              disabled={selected}
            >
              {selected
                ? t('webinar-wizard.light-activated')
                : t('webinar-wizard.theme-activate')}
            </SelectThemeButton>
          </SelectThemeOverlay>
        </StyledImage>
        <StyledLabel className="theme-label">{label}</StyledLabel>
        {selected && (
          <StyledLabel className="active-label">
            {t('webinar-wizard.active-label')}
          </StyledLabel>
        )}
      </div>
    );
  };

  return (
    <>
      <ImageUploader
        label={t('webinar-wizard.logo-label')}
        editLabel={t('webinar-wizard.edit-logo')}
        tooltipText={t('webinar-wizard.logo-tooltip')}
        formik={formik}
        valueName="logo"
        type="small"
        disabled={isImageLoading}
        callback={imageLoadingCallback}
        eventName={`${KavaEventPrefix}LogoChange`}
      />
      <ImageUploader
        label={t('webinar-wizard.banner-label')}
        editLabel={t('webinar-wizard.edit-banner')}
        tooltipText={t('webinar-wizard.banner-tooltip')}
        formik={formik}
        valueName="banner"
        withoutBackground
        disabled={isImageLoading}
        callback={imageLoadingCallback}
        eventName={`${KavaEventPrefix}BannerChange`}
      />

      <StyledThemeContainer>
        <StyledLabel className="theme-block-label">
          {t('webinar-wizard.theme-label')}
        </StyledLabel>
        <StyledThemeElement
          label={t('webinar-wizard.dark-theme')}
          image="dark_theme"
          type={'dark'}
          handleThemeSelected={handleThemeChange}
        />
        <StyledThemeElement
          label={t('webinar-wizard.light-theme')}
          image="light_theme"
          type={'light'}
          handleThemeSelected={handleThemeChange}
        />
      </StyledThemeContainer>
    </>
  );
};
